import { motion } from 'framer-motion';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { media } from '../../utils/media-queries';
import { ease } from '../../utils/easing';
import Arrow from '../../assets/svgs/arrow-next.svg';
import { useWindowSize } from '../../hooks/useWindowSize';

const ImageGallery = ({ item, i }) => {
  const size = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(size.width < 900);
  }, [size]);

  const { length } = item.images;
  const [activeImg, setActiveImg] = useState(1);

  const nextImg = () => {
    setActiveImg(activeImg + 1);
    if (activeImg === length) setActiveImg(1);
  };

  const prevImg = () => {
    setActiveImg(activeImg - 1);
    if (activeImg === 1) setActiveImg(length);
  };

  return (
    <StyledImageGallery key={`gallery-${i}`} onClick={nextImg}>
      <div className="controls">
        <div className="dots">
          {item.images.map((_, imageI) => (
            <motion.div
              key={imageI}
              onClick={() => setActiveImg(imageI + 1)}
              className="dot"
              initial={{
                background: 'var(--red-mid)',
              }}
              animate={{
                background:
                  imageI + 1 === activeImg ? 'var(--red)' : 'var(--red-mid)',
              }}
            />
          ))}
        </div>
        <p className="caption">{item.images[activeImg - 1].caption}</p>
        <button type="button" onClick={prevImg} className="prev">
          <Arrow />
        </button>
        <button type="button" onClick={nextImg} className="next">
          <Arrow />
        </button>
      </div>
      <div className="images">
        {item.images.map((image, imageI) => (
          <motion.figure
            key={imageI}
            style={{
              zIndex:
                imageI && imageI + 1 === activeImg
                  ? activeImg + 1 * 10
                  : -imageI + 1 * 10,
            }}
            animate={{
              x:
                imageI + 1 === activeImg
                  ? (isMobile ? 15 : 30) * (imageI + 3.5)
                  : (isMobile ? 15 : 30) * (imageI + 1),
              y:
                imageI + 1 === activeImg
                  ? 0
                  : (isMobile ? -7 : -15) * (imageI + 2),
            }}
            transition={{ duration: 0.4, ease: ease.outSnap }}
          >
            <GatsbyImage
              style={{ width: '100%' }}
              image={image.image.asset.gatsbyImageData}
              alt={image.image.alt}
            />
          </motion.figure>
        ))}
      </div>
    </StyledImageGallery>
  );
};

const StyledImageGallery = styled.div`
  padding-top: var(--sp-13);
  display: flex;
  flex-direction: column;

  .controls {
    color: var(--red);
    font-size: var(--fs-small);
    order: 2;

    .dots {
      transform: translateX(1.5px);
      display: flex;
      padding-bottom: var(--sp-0);
      cursor: pointer;

      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--red-mid);
        margin-right: var(--sp-0);
      }

      &::first-child {
        background: var(--red);
      }
    }

    .caption {
      max-width: 80%;
      padding-bottom: var(--sp-1-3);
    }

    .prev {
      margin-right: var(--sp-2);
      transform: scaleX(-1);
    }

    .next,
    .prev {
      circle,
      g {
        transition: 0.3s;
      }

      &:hover {
        circle {
          fill: var(--red);
        }
        g {
          stroke: var(--white);
        }
      }
    }
  }

  .images {
    height: 60vw;
    order: 1;

    figure {
      width: 70%;
      position: absolute;
      height: 45vw;
    }
  }

  @media ${media.M} {
    grid-column: 2 / 13;
    flex-direction: row;
    align-items: flex-end;

    .controls {
      width: 25%;
    }

    .images {
      order: 2;
      height: 36vw;

      figure {
        height: 35vw;
        width: 50%;
      }
    }
  }
`;

ImageGallery.propTypes = {
  item: PropTypes.object,
  i: PropTypes.number,
};

export default ImageGallery;
